import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportTokenRecharge = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);

  // Modal State
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [tokensToGrant, setTokensToGrant] = useState('20');
  const [grantReason, setGrantReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadTokenRechargeData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAgents(agents);
    } else {
      const filtered = agents.filter(agent => 
        agent.agentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.phoneNumber?.includes(searchTerm)
      );
      setFilteredAgents(filtered);
    }
  }, [searchTerm, agents]);

  const loadTokenRechargeData = async () => {
    try {
      setLoading(true);
      const data = await supportApiService.getTokenRecharge();
      if (data.success) {
        setAgents(data.listOfAgent || []);
        setFilteredAgents(data.listOfAgent || []);
      } else {
        toast.error(data.message || 'Failed to load agent token data');
      }
    } catch (error) {
      console.error('Token data load error:', error);
      toast.error('Failed to load agent token data');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantClick = (agent) => {
    setSelectedAgent(agent);
    setTokensToGrant('20');
    setGrantReason('Extra tokens granted per support request');
    setShowGrantModal(true);
  };

  const handleHistoryClick = (agentId) => {
    navigate(`/support/dashboard/token-recharge/token-recharge-history/${agentId}`);
  };

  const handleProcessGrant = async (e) => {
    e.preventDefault();
    if (!selectedAgent || !tokensToGrant || parseInt(tokensToGrant) <= 0) {
      toast.error('Please enter a valid token amount');
      return;
    }

    try {
      setProcessing(true);
      const response = await supportApiService.grantExtraTokens(
        selectedAgent.agentId, 
        tokensToGrant, 
        grantReason.trim() || 'Extra token allocation by Support'
      );
      
      if (response.success) {
        toast.success(`Successfully allocated ${tokensToGrant} extra tokens to agent ${selectedAgent.agentId}!`);
        setShowGrantModal(false);
        loadTokenRechargeData();
      } else {
        toast.error(response.message || 'Failed to allocate extra tokens');
      }
    } catch (error) {
      console.error('Error allocating extra tokens:', error);
      toast.error('Failed to allocate extra tokens');
    } finally {
      setProcessing(false);
    }
  };

  const formatName = (firstName, lastName) => {
    const first = firstName || '';
    const last = lastName || '';
    return `${first} ${last}`.trim() || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Agent Token Accounts & Support Ledger</h1>
          <p className="text-gray-600">Review agent token balances, view audit ledgers, and manually allocate extra tokens</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Agent Token Accounts</h3>
            <div className="text-sm text-gray-500">
              Total: {filteredAgents.length} agents
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fas fa-search text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  placeholder="Search by agent ID, name, email..."
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tokens Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tokens Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgents.length > 0 ? (
                  filteredAgents.map((agent, index) => (
                    <tr key={agent.agentId || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {agent.agentId ? (
                          <button
                            onClick={() => handleHistoryClick(agent.agentId)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {agent.agentId}
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.email || 'NA'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatName(agent.firstName, agent.lastName)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.phoneNumber || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                          {agent.tokens || 0} Tokens
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-800">
                          {agent.usedTokens || 0} used
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleGrantClick(agent)}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-bold transition-colors inline-flex items-center"
                        >
                          <i className="fas fa-coins mr-1"></i>
                          Allocate Tokens
                        </button>
                        <button
                          onClick={() => handleHistoryClick(agent.agentId)}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 inline-flex items-center"
                        >
                          <i className="fas fa-history mr-1"></i>
                          Audit Ledger
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      {searchTerm ? 'No agents found matching your search.' : 'No agents found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Grant Tokens Modal Popup */}
      {showGrantModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <i className="fas fa-coins text-green-600 mr-2"></i>
                Allocate Extra Tokens - {selectedAgent.agentId}
              </h3>
              <button
                onClick={() => setShowGrantModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleProcessGrant} className="space-y-4">
              <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-xs text-green-900 space-y-1">
                <div><span className="font-semibold">Agent Name:</span> {formatName(selectedAgent.firstName, selectedAgent.lastName)}</div>
                <div><span className="font-semibold">Current Balance:</span> <b>{selectedAgent.tokens || 0} tokens</b></div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Tokens to Allocate *
                </label>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  required
                  value={tokensToGrant}
                  onChange={(e) => setTokensToGrant(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm font-medium text-gray-900"
                  placeholder="e.g. 20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason / Support Note *
                </label>
                <textarea
                  rows="3"
                  required
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                  placeholder="e.g. Extra tokens granted per support ticket request"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowGrantModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold disabled:opacity-50 flex items-center"
                >
                  {processing ? 'Allocating...' : 'Allocate Tokens'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTokenRecharge;